import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Zap, Plug, Cpu, ShieldCheck, Activity, Handshake } from 'lucide-react';
import './FeatureGrid.css';

const lead = [
  {
    icon: Zap,
    title: 'Production-grade by default',
    desc: 'We don’t build demos. Every agent is built to handle real users, real edge cases, and real scale.',
  },
  {
    icon: Plug,
    title: 'Integrates with your stack',
    desc: 'WhatsApp Business API, CRMs, ERPs, databases, internal tools — we connect to what you already run.',
  },
  {
    icon: Cpu,
    title: 'Model-agnostic',
    desc: 'OpenAI, Claude, Gemini, Llama, or your own fine-tuned model. We build around your preference.',
  },
];

const compact = [
  { icon: ShieldCheck, title: 'Security & compliance first', desc: 'PII handling, access controls, and audit trails built in from day one.' },
  { icon: Activity, title: 'Observable & measurable', desc: 'Analytics on conversations handled, tasks completed, and errors caught.' },
  { icon: Handshake, title: 'End-to-end ownership', desc: 'We scope, build, deliver, and support. One team, full accountability.' },
];

export const FeatureGrid = () => {
  const reduced = useReducedMotion();

  const reveal = {
    hidden: { opacity: 0, y: reduced ? 0 : 22 },
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: reduced ? 0.2 : 0.55, delay: reduced ? 0 : i * 0.08, ease: [0.16, 1, 0.3, 1] },
    }),
  };

  return (
    <section className="why-section">
      <div className="why-container">
        <motion.h2
          className="why-title"
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
        >
          Why teams choose Liceron
        </motion.h2>

        {/* Lead row — larger, with icons */}
        <div className="why-lead">
          {lead.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                className="why-lead-item"
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-80px' }}
                custom={i}
              >
                <div className="why-icon">
                  <Icon size={22} />
                </div>
                <h3 className="why-lead-title">{f.title}</h3>
                <p className="why-lead-desc">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Compact row — inline, no containers */}
        <div className="why-compact">
          {compact.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                className="why-compact-item"
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-80px' }}
                custom={i}
              >
                <Icon size={18} className="why-compact-icon" />
                <div>
                  <h4 className="why-compact-title">{f.title}</h4>
                  <p className="why-compact-desc">{f.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
